import { Navigate, createBrowserRouter, RouterProvider } from "react-router-dom";
import { Redirect } from "react-router6-redirect";
import { useMediaQuery } from "@react-hook/media-query";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { apiAxios } from "../openapi/apiAxios";
import moment, { Moment } from "moment";

// Store imports
import { RootState, store } from "../store";
import { setUser } from "../store/userSlice";
import { ISubscriptionCheck, set } from "../store/globalSlice";
// Utility imports
import {
  checkEmptyObj,
  checkIf,
  defaultFormat,
  getUserRole,
  slugify,
  UserRoles,
  cachedApiCall,
} from "../utils";
import { createLanguageRoutes } from "../utils/createLanguageRoutes";
import { getTenant } from "../theme";
import Api from "../openapi";
import { SubscriptionResponseStatusEnum } from "../openapi/client";
import { getAccessToken, migrateOldSession, refreshAccessToken, cleanSession } from "../utils/tokenStore";

// Hook imports
import useUser from "../components/Auth/useUser";
import useSubscriptions from "../hooks/useSubscriptions";

// Eager-loaded components (always in main bundle)
import Login from "./Login";
import Join from "./Join";
import NotFound from "./NotFound";
import NavigationPlan from "../components/Navigation";
import AppRedirect from "./AppRedirect";
import Homepage from "./Homepage";
import { Layout } from "./Layout";
import Guard from "../components/Guard";
import Welcome from "../components/Spinner/welcome";

// Lazy-loaded bundles
import {
  DaySelection,
  MealSelection,
  PackageSelection,
  DeliveryOptions,
  AddressSelection,
  Summary,
  PaymentStatus,
  FindBestPlan,
  PlanPreferences,
  DietPreference,
  AllPlans,
  FeaturedPlans,
  Congrats,
} from "../routes/bundles/purchase";

import { Partners, Partner, Plan, Plans } from "../routes/bundles/partner";

import {
  Profile,
  ProfileEdit,
  ProfileHealthAnalytics,
  ProfileAddress,
  ProfileChangePassword,
  ProfileAccount,
  ProfilePlanInfo,
  ProfileCreate,
  WalletHistory,
} from "../routes/bundles/profile";

import {
  About,
  PrivacyPolicy,
  TermsAndconditions,
  TermsAndconditionsDietCenter,
  Contact,
  Support,
  DietitianAppointment,
  Onboarding,
} from "../routes/bundles/static";

import { MealPlan } from "../routes/bundles/mealplan";

// Route wrapper for lazy components
import SuspenseRoute from "../routes/SuspenseRoute";

function Main() {
  const [pageLoaded, setLoaded] = useState(false);
  const mobile = useMediaQuery("only screen and (max-width: 600px)");
  const user = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch();
  const { subscriptions } = useSubscriptions(user);
  const appUserId = localStorage.getItem("app-user-id");

  const tenant = getTenant();
  apiAxios.defaults.headers.common["app-channel"] = tenant.httpHeader;

  const userRole = useMemo(() => {
    return getUserRole(user);
  }, [user]);

  const { getUserDetail } = useUser();

  useEffect(() => {
    const subscriptionCheck: ISubscriptionCheck = {
      id: undefined,
      oneDay: false,
      threeDays: false,
      sevenDays: false,
      url: "",
      lastShipment: "",
      planUrl: "",
    };
    const getPlanDetail = async (partnerSlug: string, planSlug: string) => {
      try {
        const res = await Api.Plan.getPlanDetail(String(partnerSlug), String(planSlug));
        return res.data;
      } catch (error) {}
    };

    const getPartner = async (partnerSlug: string) => {
      try {
        const res = await Api.Partner.getBySlug(partnerSlug);
        return res.data;
      } catch (error) {}
    };

    const getPartnerLockedDays = async (partnerId: string) => {
      try {
        const res = await Api.LockedDays.getLockedDaysForPartnerBySuperAdmin(partnerId);
        return res.data.lockedDays;
      } catch (error) {}
    };

    const getOperation = async () => {
      let threeMonthGap = false;
      const subscription = user.activeSubscription!;
      subscriptionCheck.id = subscription.id;
      const active = Boolean(subscription.active && subscription.status === "ACTIVE");
      const activeSubscriptionPlanOptionId = subscription?.planOption?.id;
      const partnerSlug = slugify(String(subscription.partnerName).trim());
      const planSlug = slugify(String(subscription.planOption?.plan?.name).trim());
      const planDetail = await getPlanDetail(partnerSlug, planSlug);
      const partner = await getPartner(partnerSlug);
      const lockedDays = await getPartnerLockedDays(String(partner?.id));
      const targetPlanOption = planDetail?.options?.find((option) => option.id === activeSubscriptionPlanOptionId);
      const futureSubscriptions = subscriptions?.filter(
        (subs) => subs.active && subs.status === SubscriptionResponseStatusEnum.Future
      );
      futureSubscriptions?.sort((a, b) => (moment(a.endDate).isAfter(moment(b.endDate)) ? 1 : -1));
      const subscriptionEndDate = moment(subscription.endDate).startOf("day");

      //check if the very near future subscription is between 3 months
      if (futureSubscriptions?.length) {
        threeMonthGap = moment(futureSubscriptions[0].startDate)
          .startOf("day")
          .isSameOrBefore(subscriptionEndDate.clone().add(3, "months"));
      }

      const lockedDayStart = moment()
        .startOf("day")
        .add(Number(lockedDays) + 1, "days");

      let startDate: Moment;
      if (subscriptionEndDate.clone().add(1, "days").isBefore(lockedDayStart)) {
        startDate = lockedDayStart;
      } else {
        startDate = subscriptionEndDate.clone().add(1, "days");
      }

      futureSubscriptions?.forEach((futSubs) => {
        if (startDate.clone().add(Number(subscription.assignedDays), "days").isSameOrAfter(moment(futSubs.startDate))) {
          startDate = moment(futSubs.endDate).add(1, "days");
        }
      });

      // console.log("lockedDayStart", lockedDayStart.format(displayFormat));
      // console.log("targetPlanOption", targetPlanOption);
      // console.log("planDetail", planDetail);
      // console.log("subscription", subscription);
      if (active && !threeMonthGap) {
        // check if there is an already future plan available

        subscriptionCheck.oneDay = moment().startOf("day").add(1, "days").isSameOrAfter(subscriptionEndDate);
        subscriptionCheck.threeDays = moment().startOf("day").add(3, "days").isSameOrAfter(subscriptionEndDate);
        subscriptionCheck.sevenDays = moment().startOf("day").add(7, "days").isSameOrAfter(subscriptionEndDate);
        subscriptionCheck.lastShipment = subscriptionEndDate.format("MMMM D, YYYY");
      }

      if (subscriptionCheck.oneDay || subscriptionCheck.threeDays || subscriptionCheck.sevenDays) {
        if (targetPlanOption) {
          let url = `/partners/${partnerSlug}/${planSlug}/summary?meal=${JSON.stringify(
            targetPlanOption.mealTypeList
          )}&deliveryDays=${targetPlanOption.period}&selection=${JSON.stringify(
            planDetail?.availableDays
          )}&startDate=${startDate.format(defaultFormat)}`;
          subscriptionCheck.url = url;
          subscriptionCheck.planOptionFound = true;
          subscriptionCheck.planUrl = `/partners/${partnerSlug}/${planSlug}`;
        } else {
          const plan = partner?.plans?.find((i) => i.slug === planSlug);
          if (plan) {
            subscriptionCheck.url = `/partners/${partnerSlug}/${planSlug}`;
            subscriptionCheck.planUrl = `/partners/${partnerSlug}/${planSlug}`;
          } else {
            subscriptionCheck.url = `/partners/${partnerSlug}`;
          }
        }
        dispatch(set({ subscriptionCheck }));
      }
    };
    if (!checkEmptyObj(user) && !user.activeSubscription) {
      localStorage.removeItem("oneDayReminder");
      localStorage.removeItem("threeDaysReminder");
      localStorage.removeItem("sevenDaysReminder");
      localStorage.removeItem("activeSubscriptionToRenew");
      localStorage.removeItem("renewMade");
    }

    if (user && user.activeSubscription && subscriptions) {
      getOperation();
    }
  }, [subscriptions]);

  const appHeight = () => {
    if (mobile) {
      const doc = document.documentElement;
      doc.style.setProperty("--app-height", `${window.innerHeight}px`);
    }
  };
  window.addEventListener("resize", appHeight);
  appHeight();

  const router = useMemo(() => {
    const baseRoutes = [
      {
        element: (
          <Guard setLoaded={setLoaded}>
            <Layout />
          </Guard>
        ),
        children: [
          {
            path: "/",
            loader: async () => {
              // Check if user already exists in Redux store
              const currentUser = store.getState().user;
              if (!checkEmptyObj(currentUser) && currentUser.phoneNumber) {
                return currentUser; // User already exists, don't call API
              }

              // If we already have a valid in-memory token (e.g. loader re-runs due to router recreation), skip network calls.
              // Otherwise try migrating old cookie-based session, then fall back to normal refresh.
              const token = getAccessToken() || await migrateOldSession() || await refreshAccessToken();
              if (!token) {
                return {}; // No valid session, user is anonymous
              }

              // Set app-user-id header
              if (appUserId) {
                apiAxios.defaults.headers.common["app-user-id"] = appUserId;
              }

              try {
                // Use cached API call to prevent duplicate requests
                const user = await cachedApiCall(
                  "user_getUser",
                  async () => {
                    const res = await Api.User.getUser();
                    return res.data;
                  },
                  60000 // 60 second cache
                );

                dispatch(setUser(user));
                return user;
              } catch (error) {
                cleanSession();
                return {};
              }
            },
            children: [
              {
                index: true,
                element: <NavigationPlan />,
              },
              {
                path: "/login",
                element: (
                  <ProtectedRoute
                    outlet={<Login />}
                    authenticationPath="/"
                    isAuthenticated={userRole === UserRoles.ANONYMOUS && Boolean(mobile)}
                  />
                ),
              },
              {
                path: "/join",
                element: (
                  <ProtectedRoute
                    outlet={<Join />}
                    authenticationPath="/congrats"
                    isAuthenticated={userRole === UserRoles.ANONYMOUS}
                  />
                ),
              },
              {
                path: "/find-best-plan",
                element: (
                  <SuspenseRoute>
                    <ProtectedRoute
                      outlet={<FindBestPlan />}
                      authenticationPath="/congrats"
                      isAuthenticated={userRole === UserRoles.ANONYMOUS}
                    />
                  </SuspenseRoute>
                ),
              },
              {
                path: "/congrats",
                element: (
                  <SuspenseRoute>
                    <ProtectedRoute
                      outlet={<Congrats />}
                      authenticationPath="/"
                      isAuthenticated={userRole !== UserRoles.ANONYMOUS}
                    />
                  </SuspenseRoute>
                ),
              },
              {
                path: "/diet-preference",
                element: (
                  <SuspenseRoute>
                    <ProtectedRoute
                      outlet={<DietPreference />}
                      authenticationPath="/login"
                      isAuthenticated={userRole !== UserRoles.ANONYMOUS}
                    />
                  </SuspenseRoute>
                ),
              },
              {
                path: "/recommended-plans",
                element: (
                  <SuspenseRoute>
                    <ProtectedRoute
                      outlet={<PlanPreferences />}
                      authenticationPath="/login"
                      isAuthenticated={userRole !== UserRoles.ANONYMOUS}
                    />
                  </SuspenseRoute>
                ),
              },
              {
                path: "/all-plans",
                element: (
                  <SuspenseRoute>
                    <AllPlans />
                  </SuspenseRoute>
                ),
              },
              {
                path: "/featured-plans",
                element: (
                  <SuspenseRoute>
                    <FeaturedPlans />
                  </SuspenseRoute>
                ),
              },
              {
                path: "/payment-status",
                element: (
                  <SuspenseRoute>
                    <ProtectedRoute
                      outlet={<PaymentStatus />}
                      authenticationPath="/"
                      isAuthenticated={userRole !== UserRoles.ANONYMOUS}
                    />
                  </SuspenseRoute>
                ),
              },
              {
                path: "/meal-plan",
                element: (
                  <SuspenseRoute>
                    <ProtectedRoute
                      outlet={<MealPlan />}
                      authenticationPath={mobile ? "/login" : "/"}
                      isAuthenticated={userRole !== UserRoles.ANONYMOUS}
                    />
                  </SuspenseRoute>
                ),
              },
              {
                path: "/dietitian-appointment",
                element: (
                  <SuspenseRoute>
                    <DietitianAppointment />
                  </SuspenseRoute>
                ),
              },
              {
                path: "/partners",
                element: (
                  <SuspenseRoute>
                    {tenant.name === "rejeem" ? <Partners /> : <Redirect to={`/partners/${tenant.slug}`} />}
                  </SuspenseRoute>
                ),
              },
              {
                path: "/plans",
                element: (
                  <SuspenseRoute>
                    {tenant.name === "rejeem" ? <Redirect to="/all-plans" /> : <Partner partnerSlug={getTenant().slug} />}
                  </SuspenseRoute>
                ),
              },
              {
                path: "/partners/:partner",
                element: (
                  <SuspenseRoute>
                    <ProtectedRoute
                      outlet={<Partner />}
                      authenticationPath={mobile ? "/login" : "/"}
                      isAuthenticated={tenant.name === "diet-center" ? userRole !== UserRoles.ANONYMOUS : true}
                    />
                  </SuspenseRoute>
                ),
              },
              {
                path: "/partners/:partner/:plan/day-selection",
                element: (
                  <SuspenseRoute>
                    {mobile ? <DaySelection /> : <Redirect to="/partners/:partner/:plan" />}
                  </SuspenseRoute>
                ),
              },
              {
                path: "/partners/:partner/:plan/meal-selection",
                element: (
                  <SuspenseRoute>
                    {mobile ? (
                      tenant.name === "rejeem" ? (
                        <PackageSelection />
                      ) : (
                        <MealSelection />
                      )
                    ) : (
                      <Redirect to="/partners/:partner/:plan" />
                    )}
                  </SuspenseRoute>
                ),
              },
              {
                path: "/partners/:partner/:plan/delivery-options",
                element: (
                  <SuspenseRoute>
                    {mobile ? <DeliveryOptions /> : <Redirect to="/partners/:partner/:plan" />}
                  </SuspenseRoute>
                ),
              },
              {
                path: "/partners/:partner/:plan/address-selection",
                element: (
                  <SuspenseRoute>
                    {mobile ? <AddressSelection /> : <Redirect to="/partners/:partner/:plan" />}
                  </SuspenseRoute>
                ),
              },
              {
                path: "/partners/:partner/:plan/summary",
                element: (
                  <SuspenseRoute>{mobile ? <Summary /> : <Redirect to="/partners/:partner/:plan" />}</SuspenseRoute>
                ),
              },
              {
                path: "/partners/:partner/:plan",
                element: <SuspenseRoute>{!mobile ? <Plan /> : <Redirect to="/partners/:partner" />}</SuspenseRoute>,
              },
              {
                path: "/profile",
                element: (
                  <SuspenseRoute>
                    <ProtectedRoute
                      outlet={<Profile />}
                      authenticationPath={mobile ? "/login" : "/"}
                      isAuthenticated={userRole !== UserRoles.ANONYMOUS}
                    />
                  </SuspenseRoute>
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <ProtectedRoute
                        outlet={<ProfileAccount />}
                        authenticationPath={mobile ? "/login" : "/"}
                        isAuthenticated={userRole !== UserRoles.ANONYMOUS}
                      />
                    ),
                  },
                  {
                    path: "info",
                    element: (
                      <ProtectedRoute
                        outlet={<ProfileAccount />}
                        authenticationPath={mobile ? "/login" : "/"}
                        isAuthenticated={userRole !== UserRoles.ANONYMOUS}
                      />
                    ),
                  },
                  {
                    path: "plan-info",
                    element: (
                      <ProtectedRoute
                        outlet={<ProfilePlanInfo />}
                        authenticationPath={mobile ? "/login" : "/"}
                        isAuthenticated={userRole !== UserRoles.ANONYMOUS}
                      />
                    ),
                  },
                  {
                    path: "create-user",
                    element: (
                      <ProtectedRoute
                        outlet={<ProfileCreate onClose={() => {}} />}
                        authenticationPath={mobile ? "/login" : "/"}
                        isAuthenticated={userRole !== UserRoles.ANONYMOUS}
                      />
                    ),
                  },
                  {
                    path: "wallet",
                    element: (
                      <ProtectedRoute
                        outlet={<WalletHistory />}
                        authenticationPath={mobile ? "/login" : "/"}
                        isAuthenticated={userRole !== UserRoles.ANONYMOUS}
                      />
                    ),
                  },
                ],
              },
              {
                path: "/profile/edit",
                element: (
                  <SuspenseRoute>
                    {mobile ? (
                      <ProtectedRoute
                        outlet={<ProfileEdit />}
                        authenticationPath="/login"
                        isAuthenticated={userRole !== UserRoles.ANONYMOUS}
                      />
                    ) : (
                      <Navigate to={`/profile/info`} />
                    )}
                  </SuspenseRoute>
                ),
              },
              {
                path: "/profile/health-analytics",
                element: (
                  <SuspenseRoute>
                    {mobile ? (
                      <ProtectedRoute
                        outlet={<ProfileHealthAnalytics />}
                        authenticationPath="/login"
                        isAuthenticated={userRole !== UserRoles.ANONYMOUS}
                      />
                    ) : (
                      <Navigate to={`/profile/info`} />
                    )}
                  </SuspenseRoute>
                ),
              },
              {
                path: "/profile/address",
                element: (
                  <SuspenseRoute>
                    {mobile ? (
                      <ProtectedRoute
                        outlet={<ProfileAddress />}
                        authenticationPath="/login"
                        isAuthenticated={userRole !== UserRoles.ANONYMOUS}
                      />
                    ) : (
                      <Navigate to={`/profile/info`} />
                    )}
                  </SuspenseRoute>
                ),
              },
              {
                path: "/profile/change-password",
                element: (
                  <SuspenseRoute>
                    {mobile ? (
                      <ProtectedRoute
                        outlet={<ProfileChangePassword />}
                        authenticationPath="/login"
                        isAuthenticated={userRole !== UserRoles.ANONYMOUS}
                      />
                    ) : (
                      <Navigate to={`/profile/info`} />
                    )}
                  </SuspenseRoute>
                ),
              },
              {
                path: "/partner-application",
                element: <SuspenseRoute>{tenant.name === "rejeem" ? <Onboarding /> : <NotFound />}</SuspenseRoute>,
              },
              {
                path: "/about",
                element: <SuspenseRoute>{tenant.name === "rejeem" ? <About /> : <NotFound />}</SuspenseRoute>,
              },
              {
                path: "/privacy",
                element: (
                  <SuspenseRoute>
                    <PrivacyPolicy />
                  </SuspenseRoute>
                ),
              },
              {
                path: "/terms-and-conditions",
                element: (
                  <SuspenseRoute>
                    {tenant.name === "diet-center" ? <TermsAndconditionsDietCenter /> : <TermsAndconditions />}
                  </SuspenseRoute>
                ),
              },
              {
                path: "/support",
                element: (
                  <SuspenseRoute>
                    <Support />
                  </SuspenseRoute>
                ),
              },
              {
                path: "/contact",
                element: (
                  <SuspenseRoute>
                    <Contact />
                  </SuspenseRoute>
                ),
              },
              {
                path: "/app",
                // @ts-ignore
                element: globalThis?.App ? <Navigate to="/" /> : <AppRedirect />,
              },
              { path: "*", element: <NotFound /> },
            ],
          },
        ],
      },
    ];

    // Create language-aware routes (duplicates routes with /en and /ar prefixes)
    return createBrowserRouter(createLanguageRoutes(baseRoutes));
  }, [userRole, mobile, setLoaded]);

  return (
    <>
      {!pageLoaded ? <Welcome /> : null}
      <RouterProvider router={router} />
    </>
  );
}

export default Main;

export type ProtectedRouteProps = {
  isAuthenticated: boolean;
  authenticationPath: string;
  outlet: JSX.Element;
};

export function ProtectedRoute({ isAuthenticated, authenticationPath, outlet }: ProtectedRouteProps) {
  if (isAuthenticated) {
    return outlet;
  } else {
    const params = new URLSearchParams(document.location.search);
    const invitationCode = params.get("code");
    const mainPath = authenticationPath.split("?");
    const searchString = mainPath[1];
    const pathname = mainPath[0];
    return (
      <Navigate
        to={{
          pathname,
          search: `${checkIf(invitationCode) ? "?code=" + invitationCode : searchString ? "?" + searchString : ""}`,
        }}
      />
    );
  }
}
